import { IColumn } from "@fluentui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { NameIcons } from "../../../../../../../assets/icons";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks";
import { compileNameOfProperty } from "../../../../../../../utils";
import { CustomHeaderColumn } from "../../../../../../common/customDetailsList/customHeader";
import { CustomIcons } from "../../../../../../common/customIcons/customIcons";
import { Separator } from "../../../../../../common/separator/separator";

import { MasterBlocksColumnsStyle } from "./columnStyle.jss";
import { PopUpCodes } from "../../../../../../../common/enum/PopUpCodes";
import { MethodsSort } from "../../../../../../../common/enum/sort/Methods";
import { SortTableName } from "../../../../../../../common/enum/sort/TableName";
import { BooleanValue } from "../../../../../../common/booleanValue/booleanValue";
import { DefaultValues } from "../../../../../../../common/enum/DefaultValues";
import { fetchMasterActuationType, patchSortActuationTypesList } from "../../../../../../../redux/actions/resources/master/actuationType/actuationType";
import { IMasterActuationType } from "../../../../../../../models/resources/master/IActuationType";
import { setShowPopUpMaster } from "../../../../../../../redux/reducers/resources/master/master";

export const MasterActuationTypeColumns = (): IColumn[] => {
    const theme = useTheme();
    const styles = MasterBlocksColumnsStyle({ theme });
    const { t } = useTranslation("common", { keyPrefix: "MASTERS" });
    const dispatch = useAppDispatch();
    const { list } = useAppSelector((state) => state.masterActuationTypeList);
    const lastIndex = list ? list.length - 1 : 0;
    const columns: IColumn[] = [
        {
            key: "sort",
            minWidth: 100,
            maxWidth: 200,
            name: "",
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={""}
                    title={t("PRIORITY")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType, index) => (
                <span className={styles.actions}>
                    <CustomIcons
                        className={styles.arrow}
                        iconName={"ChevronUpSmall"}
                        isDisabled={index === 0}
                        onClick={() => {
                            dispatch(patchSortActuationTypesList(SortTableName.ActuationType, item.id ?? 0, MethodsSort.UP));
                        }}
                    />

                    <Separator size="auto" />

                    <CustomIcons
                        iconName={"ChevronDownSmall"}
                        className={styles.arrow}
                        onClick={() => {
                            dispatch(patchSortActuationTypesList(SortTableName.ActuationType, item.id ?? 0, MethodsSort.DOWN));
                        }}
                        isDisabled={index === lastIndex || (index === 0 && list?.length === 1) ? true : false}
                    />
                </span>
            ),
            isResizable: true,
        },
        {
            key: compileNameOfProperty<IMasterActuationType>("sortApp"),
            minWidth: 100,
            maxWidth: 200,
            name: t("SORT"),
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={compileNameOfProperty<IMasterActuationType>("sortApp")}
                    title={t("SORT")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => <span>{item.sortApp ?? DefaultValues.EMPTY_LIST}</span>,
            isResizable: true,
        },

        {
            key: compileNameOfProperty<IMasterActuationType>("code"),
            minWidth: 120,
            maxWidth: 200,
            name: t("CODE"),
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={compileNameOfProperty<IMasterActuationType>("code")}
                    title={t("CODE")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => <span>{item.code ?? DefaultValues.EMPTY_LIST}</span>,
            isResizable: true,
        },
        {
            key: compileNameOfProperty<IMasterActuationType>("description"),
            minWidth: 100,
            maxWidth: 200,
            name: t("DESCRIPTION"),
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={compileNameOfProperty<IMasterActuationType>("description")}
                    title={t("DESCRIPTION")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => <span>{item.description ?? DefaultValues.EMPTY_LIST}</span>,
            isResizable: true,
        },
        {
            key: compileNameOfProperty<IMasterActuationType>("actuationTypeGroupDescription"),
            minWidth: 100,
            maxWidth: 200,
            name: t("FORM.ACTUATION_GROUP"),
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={compileNameOfProperty<IMasterActuationType>("actuationTypeGroupDescription")}
                    title={t("FORM.ACTUATION_GROUP")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => <span>{item.actuationTypeGroupDescription ?? DefaultValues.EMPTY_LIST}</span>,
            isResizable: true,
        },
        {
            key: compileNameOfProperty<IMasterActuationType>("mark"),
            minWidth: 100,
            maxWidth: 200,
            name: t("MARK"),
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={compileNameOfProperty<IMasterActuationType>("mark")}
                    title={t("MARK")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => <span><BooleanValue value={item.mark}></BooleanValue></span>,
            isResizable: true,
        },

        {
            key: compileNameOfProperty<IMasterActuationType>("active"),
            minWidth: 100,
            maxWidth: 200,
            name: t("ACTIVE"),
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={compileNameOfProperty<IMasterActuationType>("active")}
                    title={t("ACTIVE")}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => <span><BooleanValue value={item.active }/> </span>,
            isResizable: true,
        },

        {
            key: "actions",
            minWidth: 100,
            maxWidth: 1000,
            name: "",
            onRenderHeader: () => (
                <CustomHeaderColumn
                    name={""}
                    title={""}
                    styles={styles}
                />
            ),
            onRender: (item: IMasterActuationType) => (
                <span className={styles.actions}>
                    <CustomIcons
                        iconName={NameIcons.EDIT}
                        onClick={() => {
                            dispatch(setShowPopUpMaster(PopUpCodes.UPDATE));
                            dispatch(fetchMasterActuationType(item.id ?? 0));
                        }}
                    />
                </span>
            ),
            isResizable: true,
        },
    ];

    return columns;
};
